/**
 * الشروط والأحكام.
 *
 * كُتبت لتُقرأ لا لتُوقَّع بلا قراءة: جمل قصيرة، وبلا حشو، وتقول ما **لا**
 * تفعله المنصّة بوضوح يماثل ما تفعله. البند الذي يخفي حدّاً يوقع مستخدماً في
 * قرار خاطئ، والوثيقة حينها تحمي الشركة على حساب من تخدمه — وهذا فشل لا نجاح.
 *
 * ⚠️ صياغة أولية معدّة داخلياً، **لم تُراجَع من محامٍ سعودي**. تُراجَع قبل أي
 *    تحصيل مالي أو تعاقد مؤسسي.
 */

import { Link } from 'react-router-dom';
import { color } from '@/design/tokens';
import { LegalPage, LegalSection, L, Notice, P, LEGAL_CONTACT } from './LegalPage';

export default function TermsRoute(): JSX.Element {
  return (
    <LegalPage
      stamp="NITAQ / TERMS"
      title="الشروط والأحكام"
      subtitle="ما تقدّمه نطاق، وما لا تقدّمه، وحدود استخدامها."
      testId="terms-page"
    >
      <Notice>
        <strong style={{ color: color.text }}>الخلاصة في سطرين:</strong> نطاق أداة تحليل
        بيانات تساعدك على قراءة السوق. ليست تقييماً معتمداً، ولا توصية بالشراء أو البيع،
        ولا تتحمّل مسؤولية قرارك.
      </Notice>

      <LegalSection number="١" title="طبيعة الخدمة">
        <P>
          نطاق منصّة تحليل بيانات عقارية. تقرأ صفقات مسجّلة رسمياً وتُخرج مؤشرات إحصائية
          تساعد على فهم السوق: نطاقاً سعرياً للحي، وقوة الدليل خلفه، وصفقات مماثلة،
          ومؤشرات فرصة واتجاه.
        </P>
        <P>
          <strong style={{ color: color.text }}>نطاق ليست:</strong>
        </P>
        <L
          items={[
            'جهة تقييم عقاري معتمدة، ولا تصدر تقارير تقييم.',
            'وسيطاً عقارياً، ولا تعرض عقارات للبيع أو الشراء.',
            'مستشاراً مالياً أو استثمارياً، ولا تقدّم توصية بشراء أو بيع.',
            'مصدراً رسمياً للبيانات — هي طبقة تحليل فوق مصادر رسمية.',
          ]}
        />
      </LegalSection>

      <LegalSection number="٢" title="طبيعة المخرجات وحدودها">
        <P>
          كل رقم تراه في نطاق <strong style={{ color: color.text }}>تقدير آلي</strong> ناتج
          عن نماذج إحصائية مُعلَنة الإصدار، وليس رأياً مهنياً ولا قيمة مؤكّدة.
        </P>
        <P>
          حين لا تكفي البيانات لإصدار نتيجة موثوقة، تمتنع المنصّة وتقول ذلك صراحةً بدل أن
          تعطي رقماً ضعيف السند. الامتناع نتيجةٌ مقصودة لا خلل.
        </P>
        <P>
          تفاصيل الحدود والمصادر في صفحة{' '}
          <Link to="/disclaimer" style={{ color: color.teal }}>
            إخلاء المسؤولية
          </Link>
          .
        </P>
      </LegalSection>

      <LegalSection number="٣" title="ما لا تصلح له المخرجات">
        <Notice>
          مخرجات نطاق <strong style={{ color: color.text }}>لا تصلح</strong> للأغراض
          البنكية أو التمويلية أو القضائية أو النظامية، ولا تُقبل بديلاً عن تقرير تقييم
          معتمد. للحصول على تقييم معتمد، راجع مقيّماً مرخّصاً من الهيئة السعودية للمقيمين
          المعتمدين.
        </Notice>
        <P>
          استخدام مخرجات المنصّة في أيٍّ من هذه الأغراض مخالفة لهذه الشروط، وتقع مسؤوليتها
          كاملةً على المستخدم.
        </P>
      </LegalSection>

      <LegalSection number="٤" title="مسؤولية المستخدم عن قراره">
        <P>
          القرار الاستثماري قرارك وحدك. نطاق تعرض ما تثبته البيانات، ولا تعرف ظروفك ولا
          خصائص العقار الفعلية ولا ما استجدّ بعد آخر تحديث للبيانات.
        </P>
        <P>
          قبل أي التزام مالي: عاين العقار، وتحقّق من صكّه وقيوده ومخططه، واستعن بمختصّ
          مرخّص. لا تبنِ قراراً على رقم واحد من أي مصدر — بما في ذلك نحن.
        </P>
      </LegalSection>

      <LegalSection number="٥" title="الاستخدام المسموح والممنوع">
        <P>يُسمح باستخدام المنصّة لأغراضك الشخصية أو داخل منشأتك.</P>
        <P>
          <strong style={{ color: color.text }}>ويُمنع:</strong>
        </P>
        <L
          items={[
            'الاستخراج الآلي للبيانات (سحب برمجي، أو نسخ منهجي واسع).',
            'إعادة بيع البيانات أو المخرجات أو إعادة نشرها للعموم.',
            'إعادة تقديم المخرجات على أنها تقييم معتمد أو رأي مهني.',
            'محاولة الوصول إلى حسابات أو بيانات لا تخصّك.',
            'أي استخدام يخالف أنظمة المملكة العربية السعودية.',
          ]}
        />
      </LegalSection>

      <LegalSection number="٦" title="الحساب">
        <P>
          إنشاء الحساب برقم جوال. أنت مسؤول عن حماية وصولك إليه، وعن كل نشاط يتم من خلاله.
        </P>
        <P>
          يحق لنا إيقاف حساب يخالف هذه الشروط، مع إشعارك بالسبب متى كان ذلك ممكناً.
        </P>
      </LegalSection>

      <LegalSection number="٧" title="الاشتراكات والمقابل المالي">
        <P>
          الخدمة متاحة حالياً <strong style={{ color: color.text }}>بلا مقابل مالي</strong>،
          ولا توجد اشتراكات ولا بوابة دفع.
        </P>
        <P>
          إن أُضيفت خطط مدفوعة مستقبلاً، تُعلَن شروطها وأسعارها قبل تفعيلها، ولا يُخصم أي
          مبلغ بلا موافقة صريحة منك.
        </P>
      </LegalSection>

      <LegalSection number="٨" title="توفّر الخدمة وتغيّرها">
        <P>
          نطاق منصّة قيد التطوير المستمر. قد تتغيّر الميزات، وقد تتوقّف الخدمة مؤقتاً
          للصيانة أو لأسباب خارجة عن إرادتنا، ولا نضمن توفّراً متواصلاً بلا انقطاع.
        </P>
        <P>
          وقد يتغيّر ما تُخرجه النماذج مع تحسّنها أو مع تحديث البيانات — فالنتيجة اليوم قد
          تختلف عن نتيجة الأمس لنفس المدخلات، وهذا سلوك مقصود لا خطأ.
        </P>
      </LegalSection>

      <LegalSection number="٩" title="حدود المسؤولية">
        <P>
          تُقدَّم الخدمة «كما هي». لا نضمن دقّة كل رقم ولا اكتمال كل بيان ولا خلوّ المنصّة
          من الخطأ.
        </P>
        <P>
          ولا نتحمّل مسؤولية أي خسارة مباشرة أو غير مباشرة تنشأ عن الاعتماد على مخرجات
          المنصّة في قرار استثماري أو تعاقدي.
        </P>
      </LegalSection>

      <LegalSection number="١٠" title="الملكية الفكرية">
        <P>
          تصميم المنصّة وهويتها ونماذجها التحليلية وطريقة عرضها مملوكة لنطاق. البيانات
          الأساسية تعود لمصادرها، وحقوقها لأصحابها.
        </P>
      </LegalSection>

      <LegalSection number="١١" title="النظام الواجب التطبيق">
        <P>
          تخضع هذه الشروط لأنظمة المملكة العربية السعودية، وتختصّ الجهات القضائية المختصّة
          في المملكة بأي نزاع ينشأ عنها.
        </P>
      </LegalSection>

      <LegalSection number="١٢" title="تعديل الشروط والتواصل">
        <P>
          قد تُحدَّث هذه الشروط. النسخة السارية هي المنشورة على هذه الصفحة بتاريخها الظاهر
          في الأعلى، ونُعلن التغييرات الجوهرية داخل المنصّة.
        </P>
        <P>
          لأي استفسار:{' '}
          <a href={`mailto:${LEGAL_CONTACT}`} style={{ color: color.teal }}>
            {LEGAL_CONTACT}
          </a>
        </P>
      </LegalSection>
    </LegalPage>
  );
}
